/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import user from 'js/lib/user';

import { InlineNotification, TextField, Typography2 } from '@coursera/cds-core';
import { Button, useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';
import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import SettingsActionCreators from 'bundles/account-settings/actions/SettingsActionCreators';
import SettingsServerActionCreators from 'bundles/account-settings/actions/SettingsServerActionCreators';
import SettingsConstants from 'bundles/account-settings/constants/SettingsConstants';
import type { UpdateStatusState } from 'bundles/account-settings/types/Settings';
import { USER_EMAIL_REGEX_PATTERN } from 'bundles/user-account/common/constants';

import _t from 'i18n!nls/account-settings';

const { UpdateStates, Sections } = SettingsConstants;

type Props = {
  updateStatus: { state: UpdateStatusState };
  secondaryEmail: UserEmail | undefined;
  hasGeneratedPassword: boolean;
  ssoOrgSlug?: string | undefined;
};

const inputName = 'account_settings_create_password';
const createPasswordButtonName = 'account_setting_create_password_btn';
const EMAIL_REGEX = new RegExp(`^${USER_EMAIL_REGEX_PATTERN}$`);

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  headingGroup: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  heading: css`
    margin: 0;
  `,
  subtitle: css`
    margin: 0;
  `,
  input: css`
    width: 100%;
  `,
  notificationSpacing: css`
    margin-block-end: var(--cds-spacing-100);
  `,
  liveRegion: css`
    width: 100%;

    &:not(:empty) {
      margin-block-end: var(--cds-spacing-200);
    }
  `,
  buttonRow: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  `,
};

const AlternativeEmail = ({ updateStatus, secondaryEmail, hasGeneratedPassword, ssoOrgSlug }: Props) => {
  const [email, setEmail] = useState(secondaryEmail?.emailAddress || '');
  const [emailFieldError, setEmailFieldError] = useState('');
  const emailInputRef = useRef<HTMLInputElement>(null);
  const enterpriseProps = ssoOrgSlug ? { enterprise: { organizationSlug: ssoOrgSlug } } : {};
  const addRecoveryEmailButtonRef = useVisibilityTracker('view_button', {
    button: { name: createPasswordButtonName },
    ...enterpriseProps,
  });

  const track = useTracker();

  useEffect(() => {
    setEmail(secondaryEmail?.emailAddress || '');
  }, [secondaryEmail]);

  const trackCreatePasswordInputEvent = (status: 'success' | 'failure', error?: { status?: number | string }) => {
    track('validate_text_input', {
      textInput: { name: inputName },
      status,
      ...(error && { errorCode: `${error?.status ?? 'network_error'}` }),
      ...enterpriseProps,
    });
  };

  const onSubmit = () => {
    if (!email || !EMAIL_REGEX.test(email)) {
      setEmailFieldError(_t('Please enter a valid email address.'));
      emailInputRef.current?.focus();
      track('validate_text_input', {
        textInput: { name: inputName },
        status: 'failure',
        errorCode: 'invalid_email',
        ...enterpriseProps,
      });
      SettingsServerActionCreators.updateInvalidEmail(Sections.PERSONAL_ACCOUNT);
      return;
    }

    const handleSuccessTracking = () => {
      trackCreatePasswordInputEvent('success');
    };

    const handleErrorTracking = (error: { status?: number | string }) => {
      trackCreatePasswordInputEvent('failure', error);
    };

    if (!secondaryEmail && hasGeneratedPassword) {
      SettingsActionCreators.addAlternativeEmail(email)
        .then(() => SettingsActionCreators.requestResetPassword())
        .then(handleSuccessTracking)
        .catch(handleErrorTracking);
    } else if (hasGeneratedPassword) {
      SettingsActionCreators.requestResetPassword().then(handleSuccessTracking).catch(handleErrorTracking);
    } else if (!secondaryEmail) {
      SettingsActionCreators.addAlternativeEmail(email).then(handleSuccessTracking).catch(handleErrorTracking);
    }
  };

  const handleSaveButton = () => {
    onSubmit();
  };

  const onEmailFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setEmailFieldError('');
  };

  const onEmailFieldBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const emailVal = event.target.value;
    if (!emailVal || !EMAIL_REGEX.test(emailVal)) {
      setEmailFieldError(_t('Please enter a valid email address.'));
      SettingsServerActionCreators.updateInvalidEmail(Sections.PERSONAL_ACCOUNT);
    } else {
      setEmailFieldError('');
      SettingsActionCreators.resetSection(Sections.PERSONAL_ACCOUNT);
    }
  };

  const updateSucceeded = updateStatus.state === UpdateStates.SUCCEEDED;
  const isUpdating = updateStatus.state === UpdateStates.UPDATING;
  const isFailed =
    updateStatus.state === UpdateStates.FAILED || updateStatus.state === UpdateStates.FAILED_EMAIL_IN_USE;
  const shouldShowBanner = !!secondaryEmail && hasGeneratedPassword;
  const hasExistingEmailAndPassword = !!secondaryEmail && !hasGeneratedPassword;
  const errorMessage =
    updateStatus.state === UpdateStates.FAILED_EMAIL_IN_USE
      ? _t('This email is already in use.')
      : _t('There was an issue saving your alternative email. Please try again.');

  return (
    <div css={styles.container}>
      <div css={styles.headingGroup}>
        <Typography2 variant="subtitleLarge" component="h2" css={styles.heading}>
          {_t('Alternative email')}
        </Typography2>
        <Typography2 variant="bodySecondary" component="p" color="supportText" css={styles.subtitle}>
          {_t(
            'Add your personal account here, so you’ll still have access to Coursera courses after you leave your current company.'
          )}
        </Typography2>
      </div>

      {shouldShowBanner && (
        <InlineNotification
          severity="warning"
          css={styles.notificationSpacing}
          inlineAction={
            <Button
              size="small"
              variant="ghost"
              onClick={handleSaveButton}
              loading={isUpdating}
              trackingData={{
                button: { name: createPasswordButtonName },
                ...enterpriseProps,
              }}
            >
              {_t('Re-send the email')}
            </Button>
          }
        >
          {_t('Your personal account is not set up yet, please re-send the email and follow the instructions.')}
        </InlineNotification>
      )}

      <TextField
        css={styles.input}
        inputRef={emailInputRef}
        value={email}
        onChange={onEmailFieldChange}
        onBlur={onEmailFieldBlur}
        placeholder={_t('Enter personal email')}
        aria-label={_t('Alternative email')}
        disabled={updateSucceeded || !!secondaryEmail}
        validationStatus={emailFieldError ? 'error' : undefined}
        validationLabel={emailFieldError || undefined}
      />

      <div css={styles.buttonRow}>
        <div css={styles.liveRegion} role="status" aria-live="polite" aria-atomic="true">
          {updateSucceeded && (
            <InlineNotification severity="success">
              {hasGeneratedPassword ? (
                <FormattedHTMLMessage
                  message={_t(
                    'An email has been sent to {email}. Please follow the instructions in the link to set up your personal account.'
                  )}
                  email={<b>{user.get().email_address}</b>}
                />
              ) : (
                _t('Alternative email added!')
              )}
            </InlineNotification>
          )}
        </div>
        <div css={styles.liveRegion} role="alert" aria-live="assertive" aria-atomic="true">
          {isFailed && <InlineNotification severity="error">{errorMessage}</InlineNotification>}
        </div>
        <Button
          ref={addRecoveryEmailButtonRef as React.Ref<HTMLButtonElement>}
          variant="secondary"
          size="medium"
          onClick={handleSaveButton}
          loading={isUpdating}
          disabled={hasExistingEmailAndPassword || updateSucceeded}
          trackingData={{
            button: { name: createPasswordButtonName },
            ...enterpriseProps,
          }}
        >
          {hasGeneratedPassword ? _t('Create Password') : _t('Save')}
        </Button>
      </div>
    </div>
  );
};

export default AlternativeEmail;
